/**
 * PerformanceChart — Plotly equity curve: thesis portfolio vs SPY vs QQQ.
 * Normalized to 100 at start for clean comparison.
 */
import Plot from 'react-plotly.js';

interface CurvePoint {
  date: string;
  portfolio: number;
  spy?: number | null;
  qqq?: number | null;
}

interface Props {
  equityCurve: CurvePoint[];
  title?: string;
}

export function PerformanceChart({ equityCurve, title }: Props) {
  if (!equityCurve || equityCurve.length < 2) {
    return (
      <div className="card p-3 text-xs text-ink-mute">
        Insufficient data for performance chart.
      </div>
    );
  }

  const x = equityCurve.map((p) => p.date);
  const portfolio = equityCurve.map((p) => p.portfolio);
  const spy = equityCurve.map((p) => p.spy ?? null);
  const qqq = equityCurve.map((p) => p.qqq ?? null);

  const traces: Plotly.Data[] = [
    {
      x,
      y: portfolio,
      type: 'scatter',
      mode: 'lines',
      name: 'Thesis Portfolio',
      line: { color: '#6366f1', width: 2 },
    },
  ];

  if (spy.some((v) => v !== null)) {
    traces.push({
      x,
      y: spy.map((v) => v ?? undefined) as Plotly.Datum[],
      type: 'scatter',
      mode: 'lines',
      name: 'SPY (S&P 500)',
      line: { color: '#94a3b8', width: 1.5, dash: 'dash' },
    });
  }

  if (qqq.some((v) => v !== null)) {
    traces.push({
      x,
      y: qqq.map((v) => v ?? undefined) as Plotly.Datum[],
      type: 'scatter',
      mode: 'lines',
      name: 'QQQ (Nasdaq)',
      line: { color: '#f59e0b', width: 1.5, dash: 'dot' },
    });
  }

  return (
    <div className="card p-3">
      <div className="text-[11px] text-ink-mute uppercase mb-2">
        {title || 'historical performance vs indices (normalized to 100)'}
      </div>
      <Plot
        data={traces}
        layout={{
          height: 300,
          margin: { t: 20, b: 40, l: 50, r: 20 },
          xaxis: { title: { text: '' }, showgrid: false },
          yaxis: { title: { text: 'Value (base 100)' }, showgrid: true, gridcolor: '#e2e8f0' },
          legend: { orientation: 'h', y: -0.15 },
          paper_bgcolor: 'transparent',
          plot_bgcolor: 'transparent',
          font: { size: 11, color: '#64748b' },
        }}
        config={{ displayModeBar: false, responsive: true }}
        style={{ width: '100%' }}
      />
    </div>
  );
}
